import React, { useState, useCallback, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { API_BASE_URL } from '../../lib/apiConfig';
import { apiFetch } from '../../lib/apiFetch';
import { useAuth } from '../../contexts/AuthContext';
import ContactSyncModal, { DeviceContact } from './ContactSyncModal';

// ─── Constants ────────────────────────────────────────────────────────────────

const LS_KEY = 'contact_sync_cache'; // { count: number; lastSync: number }

// ─── Animations ──────────────────────────────────────────────────────────────

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Styled components ────────────────────────────────────────────────────────

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #F0FAF8;
  border: 1.5px solid #BDE0D9;
  border-radius: 14px;
  margin: 0 0 10px;
  animation: ${fadeSlideIn} 0.3s ease;
  cursor: pointer;
  transition: background 0.15s;
  &:active { background: #e0f5f2; }
`;

const IconWrap = styled.div`
  width: 44px; height: 44px;
  border-radius: 12px;
  background: #0E8C7F;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const CountRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 5px;
`;

const BigNum = styled.span`
  font-size: 22px;
  font-weight: 800;
  color: #0E8C7F;
  line-height: 1;
`;

const NumLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #0a0f1c;
`;

const SubText = styled.div`
  font-size: 11.5px;
  color: #64748b;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ImportPill = styled.button`
  background: #0E8C7F;
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 7px 14px;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity 0.15s;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SpinnerSm = styled.div`
  width: 14px; height: 14px;
  border: 2px solid rgba(14,140,127,0.25);
  border-top-color: #0E8C7F;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

// ─── Helper: normalize phone ───────────────────────────────────────────────────

const normalizePhone = (raw: string): string => raw.replace(/\D/g, '').slice(-9);

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  onImported: () => void;
}

const ContactSyncCard: React.FC<Props> = ({ onImported }) => {
  const { session } = useAuth();
  const [unsyncedContacts, setUnsyncedContacts] = useState<DeviceContact[]>([]);
  const [cachedCount, setCachedCount] = useState<number | null>(null);
  const [checking, setChecking] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [supported] = useState(() => 'contacts' in navigator);

  // Load cached count from localStorage on mount
  useEffect(() => {
    if (!supported) return;
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const { count } = JSON.parse(raw);
        if (typeof count === 'number' && count > 0) setCachedCount(count);
      }
    } catch {
      // ignore
    }
  }, [supported]);

  const handleSync = useCallback(async () => {
    if (checking) return;
    setChecking(true);
    try {
      // 1. Request device contacts
      const deviceContacts: any[] = await (navigator as any).contacts.select(
        ['name', 'tel'],
        { multiple: true }
      );

      if (!deviceContacts || deviceContacts.length === 0) {
        setChecking(false);
        return;
      }

      // 2. Fetch existing phones from backend
      const res = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/phones`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch phones');
      const { data: existingPhones }: { data: string[] } = await res.json();
      const existingSet = new Set(existingPhones);

      // 3. Diff: find device contacts not in the system
      const unmatched: DeviceContact[] = [];
      for (const dc of deviceContacts) {
        const rawName: string = (dc.name?.[0] || '').trim();
        if (!rawName) continue;
        const phones: string[] = dc.tel || [];
        for (const rawPhone of phones) {
          const normalized = normalizePhone(rawPhone);
          if (normalized.length < 8) continue;
          if (!existingSet.has(normalized)) {
            unmatched.push({ name: normalized, phone: normalized, rawPhone, rawName });
            break; // one entry per contact
          }
        }
      }

      setUnsyncedContacts(unmatched);
      const count = unmatched.length;
      setCachedCount(count);
      localStorage.setItem(LS_KEY, JSON.stringify({ count, lastSync: Date.now() }));

      if (count > 0) {
        setShowModal(true);
      }
    } catch (err) {
      console.error('[ContactSyncCard] sync error', err);
    } finally {
      setChecking(false);
    }
  }, [checking]);

  const handleImported = useCallback((count: number) => {
    const newCount = Math.max(0, (cachedCount ?? 0) - count);
    setCachedCount(newCount > 0 ? newCount : null);
    localStorage.setItem(LS_KEY, JSON.stringify({ count: newCount, lastSync: Date.now() }));
    onImported();
  }, [cachedCount, onImported]);

  // Don't render on non-supporting browsers (must be after all hooks)
  if (!supported) return null;

  const displayCount = unsyncedContacts.length > 0 ? unsyncedContacts.length : cachedCount;
  const hasCount = displayCount !== null && displayCount > 0;

  return (
    <>
      <Card onClick={handleSync}>
        <IconWrap>
          {/* Phone-book glyph */}
          <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
            <rect x="4" y="2" width="16" height="20" rx="2" stroke="#fff" strokeWidth="2"/>
            <circle cx="12" cy="10" r="3" stroke="#fff" strokeWidth="2"/>
            <path d="M7 19c.5-3 2.5-5 5-5s4.5 2 5 5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </IconWrap>

        <Body>
          <CountRow>
            {checking ? (
              <SpinnerSm />
            ) : hasCount ? (
              <BigNum>{displayCount}</BigNum>
            ) : null}
            <NumLabel>{checking ? 'Verificando...' : hasCount ? 'do telefone' : 'Sincronizar agenda'}</NumLabel>
          </CountRow>
          <SubText>
            {checking
              ? 'Comparando com o sistema...'
              : hasCount
                ? `${displayCount} contato${displayCount !== 1 ? 's' : ''} ainda não importado${displayCount !== 1 ? 's' : ''}`
                : 'Toque para comparar sua agenda com o sistema'}
          </SubText>
        </Body>

        <ImportPill
          disabled={checking}
          onClick={e => { e.stopPropagation(); handleSync(); }}
        >
          {checking ? '...' : hasCount ? 'Importar →' : 'Verificar →'}
        </ImportPill>
      </Card>

      {showModal && unsyncedContacts.length > 0 && (
        <ContactSyncModal
          contacts={unsyncedContacts}
          onClose={() => setShowModal(false)}
          onImported={handleImported}
        />
      )}
    </>
  );
};

export default ContactSyncCard;
